"use client";

import React from "react";
import { motion } from "framer-motion";
import { Building2, Truck, Calendar, ShieldAlert, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const venueTypes = [{
  title: "Bars & Entertainment Venues",
  focus: "Focus on space, operating layout and long-term parts availability.",
  icon: Building2,
  tag: "Permanent Installation"
}, {
  title: "Mobile Event Operators",
  focus: "Focus on transport, setup, packed dimensions and inflatable condition.",
  icon: Truck,
  tag: "High Mobility"
}, {
  title: "Seasonal or Occasional Use",
  focus: "Focus on storage, complete package contents and straightforward setup requirements.",
  icon: Calendar,
  tag: "Intermittent Run"
}];
export default function SelectionGuide() {
  return <section id="commercialselectionguide" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Prominent Sales Only Banner */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        ease: "easeOut"
      }} className="mb-14 border border-primary/40 bg-card p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-none shadow-xs">
          <div className="flex items-center gap-3">
            <div className="bg-primary/10 border border-primary/30 p-2 text-primary shrink-0 rounded-none">
              <ShieldAlert className="size-5" />
            </div>
            <div>
              <span className="font-mono text-xs uppercase tracking-wider text-primary block font-semibold">
                Equipment Sales Policy
              </span>
              <p className="text-sm sm:text-base font-semibold text-foreground tracking-tight">
                Mechanical Bull Shop sells equipment. It does not offer mechanical bull rentals.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Section Header & Narrative */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 items-start">
          <div className="lg:col-span-6">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold uppercase tracking-tight text-foreground leading-[1.08]">
              Inflatable Rodeo Bull Systems for Commercial Use
            </h2>
          </div>

          <div className="lg:col-span-6 space-y-4 text-muted-foreground text-base sm:text-lg leading-relaxed border-l-2 border-border pl-6 lg:pl-8">
            <p className="text-foreground font-medium">
              An inflatable rodeo bull system may be used by entertainment venues, event operators and other businesses, but the right package depends on how the equipment will actually be handled and operated.
            </p>
            <p>
              A permanent venue may focus more on operating footprint, controller features and long-term parts availability. A mobile operator may care more about packed size, transport, repeated setup and inflatable handling.
            </p>
            <p className="text-sm sm:text-base border-t border-border pt-4 text-muted-foreground">
              Compare the actual equipment package against your intended use rather than choosing by appearance alone.
            </p>
          </div>
        </div>

        {/* 3 Bordered Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-14">
          {venueTypes.map((item, i) => {
          const Icon = item.icon;
          return <div key={item.title} data-index={i} className="h-full">
                <Card className="h-full rounded-none border border-border bg-card shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/60 hover:shadow-md flex flex-col justify-between">
                  <div>
                    <CardHeader className="p-6 border-b border-border bg-muted/40">
                      <div className="flex items-center justify-end mb-4">
                        <span className="font-mono text-2xs uppercase tracking-wider text-muted-foreground border border-border px-2 py-0.5 bg-background">
                          {item.tag}
                        </span>
                      </div>
                      <div className="size-12 rounded-none bg-background border border-border flex items-center justify-center text-foreground mb-4">
                        <Icon className="size-6 text-primary" />
                      </div>
                      <CardTitle className="text-xl sm:text-2xl font-serif font-bold uppercase tracking-tight text-foreground leading-snug">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-6">
                      <p className="text-sm sm:text-base text-muted-foreground leading-normal">
                        {item.focus}
                      </p>
                    </CardContent>
                  </div>
                </Card>
              </div>;
        })}
        </div>

        {/* Bottom Operational Action Strip */}
        <div className="border border-border bg-card p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 rounded-none">
          <div>
            <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-foreground mb-1">
              Need technical sizing or controller specs?
            </h3>
            <p className="text-sm text-muted-foreground">
              Review inflatable footprints and available machine options.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="outline" className="rounded-none border-border font-medium w-full sm:w-auto">
              <Link to="/MechanicalBullBuyingGuidePage">
                Read Buying Guide
              </Link>
            </Button>
            <Button asChild className="rounded-none font-medium w-full sm:w-auto">
              <Link to="/RequestQuotePage">
                Request Specifications
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}